import React from 'react';
import { ReportSection, ReportSubsection } from '../framework';
import { CalloutBox, StatCard, CalculationBreakdown, ComparisonGrid, DataTable } from '../primitives';
import { SectionWithResultsProps } from './types';

export const RentOptionSection: React.FC<SectionWithResultsProps> = ({
  assumptions,
  metrics,
  formatCurrency,
  finalYear
}) => {
  // Calculate rent scenarios for the "What If" table
  // This mirrors the main calculation in calculations.ts for consistency
  const calcRentalOutcome = (monthlyRent: number, rentGrowth: number) => {
    const basePropertyValue = assumptions.propertyValue;
    const appreciation = assumptions.propertyAppreciation;
    const year10PropertyValue = basePropertyValue * Math.pow(1 + appreciation / 100, 10);

    // Depreciation (25% building value over 27.5 years)
    const buildingValue = basePropertyValue * 0.25;
    const annualDepreciation = buildingValue / 27.5;
    const totalDepreciation = annualDepreciation * 10;

    // Tax drag on reinvested rental income (same as main calculations)
    const SP500_DIVIDEND_YIELD = 0.013;
    const dividendTaxRate = assumptions.capitalGainsRate / 100;
    const bondInterestTaxRate = assumptions.rentalIncomeTaxRate / 100;
    const equityDividendDrag = SP500_DIVIDEND_YIELD * dividendTaxRate;
    const bondInterestDrag = 0.04 * bondInterestTaxRate;
    const effectiveStockReturn = assumptions.stockReturns - (equityDividendDrag * 100);
    const effectiveBondReturn = assumptions.bondReturns - (bondInterestDrag * 100);
    const blendedReturn = (0.8 * effectiveStockReturn) + (0.2 * effectiveBondReturn);

    // Prop 13: assessed value grows at max 2%/year nominal after reassessment
    const PROP_13_MAX_GROWTH_RATE = 0.02;
    const nominalAppreciation = appreciation / 100 + assumptions.inflationRate / 100;
    const assessedGrowthRate = Math.min(nominalAppreciation, PROP_13_MAX_GROWTH_RATE);

    let portfolio = 0;
    const taxRate = assumptions.rentalIncomeTaxRate / 100;

    for (let year = 1; year <= 10; year++) {
      const yearPropertyValue = basePropertyValue * Math.pow(1 + appreciation / 100, year);
      const yearAssessedValue = basePropertyValue * Math.pow(1 + assessedGrowthRate, year);
      const yearRent = monthlyRent * 12 * Math.pow(1 + rentGrowth / 100, year);
      const vacancyLoss = yearRent * assumptions.vacancyRate / 100;
      const grossRentalIncome = yearRent - vacancyLoss;

      // Property tax based on assessed value (Prop 13), not market value
      const propertyTax = yearAssessedValue * assumptions.propertyTaxRate / 100;
      const maintenance = yearPropertyValue * assumptions.maintenanceRate / 100;
      const managementCost = grossRentalIncome * assumptions.managementFee / 100;

      const netRentalIncome = grossRentalIncome - propertyTax - assumptions.insuranceCost - maintenance - managementCost;

      const taxableIncome = Math.max(0, netRentalIncome - annualDepreciation);
      const tax = taxableIncome * taxRate;
      const afterTax = netRentalIncome - tax;

      portfolio = portfolio * (1 + blendedReturn / 100) + afterTax;
    }

    const grossEquity = year10PropertyValue + portfolio - assumptions.rentalSetupCosts;

    // Exit costs and taxes (for net liquidation value)
    const FEDERAL_DEPRECIATION_RECAPTURE_RATE = 0.25;
    const CA_STATE_TAX_RATE = 0.123;
    const depreciationRecaptureTax = totalDepreciation * (FEDERAL_DEPRECIATION_RECAPTURE_RATE + CA_STATE_TAX_RATE);
    const appreciationSinceInheritance = year10PropertyValue - basePropertyValue;
    const capitalGainsTax = appreciationSinceInheritance > 0 ? appreciationSinceInheritance * (assumptions.capitalGainsRate / 100) : 0;
    const sellingCostsAtExit = year10PropertyValue * (assumptions.sellingCosts / 100);

    return grossEquity - depreciationRecaptureTax - capitalGainsTax - sellingCostsAtExit;
  };

  const baseRent = Math.round(assumptions.monthlyRent / 1000) * 1000; // Round to nearest $1K for display
  const scenarios = [
    { label: 'Baseline', rent: assumptions.monthlyRent, growth: assumptions.rentGrowth, desc: 'Current assumptions' },
    { label: 'Higher Rent', rent: baseRent + 1000, growth: assumptions.rentGrowth, desc: `$${((baseRent + 1000) / 1000).toFixed(0)}K/mo rent` },
    { label: 'Higher Growth', rent: assumptions.monthlyRent, growth: 2.0, desc: '2% rent growth' },
    { label: 'Both', rent: baseRent + 1000, growth: 2.0, desc: `$${((baseRent + 1000) / 1000).toFixed(0)}K + 2% growth` },
  ];

  const baseline = calcRentalOutcome(assumptions.monthlyRent, assumptions.rentGrowth);

  return (
    <ReportSection id="rent" title="Option A: Keep & Rent" subtitle="Becoming a landlord">
      <p className="text-gray-700 mb-6">
        In this scenario, you retain ownership of the property and rent it to tenants. Let's break down
        exactly what this looks like financially.
      </p>

      <ReportSubsection title="The Two Income Streams">
        <p className="text-gray-700 mb-4">
          Your wealth grows through two channels:
        </p>

        <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
          <li><strong>Rental income (net of expenses):</strong> The cash you actually pocket each year after
          paying all the costs of being a landlord.</li>
          <li><strong>Property appreciation:</strong> The (hopefully) increasing value of the house itself.</li>
        </ol>

        <p className="text-gray-700">
          We assume you'll invest the net rental income in the stock market rather than spending it. This
          maximizes the comparison—both options have money invested in stocks, the question is whether you
          also own the house.
        </p>
      </ReportSubsection>

      <ReportSubsection title="The Expense Breakdown">
        <p className="text-gray-700 mb-4">
          Being a landlord is not free. Here's where the money goes each year:
        </p>

        <CalculationBreakdown
          steps={[
            { label: `Property Taxes (${assumptions.propertyTaxRate}% of assessed value)`, value: assumptions.propertyValue * assumptions.propertyTaxRate / 100 },
            { label: 'Insurance (coastal CA—see note below)', value: assumptions.insuranceCost },
            { label: `Maintenance (${assumptions.maintenanceRate}% of value)`, value: assumptions.propertyValue * assumptions.maintenanceRate / 100 },
            { label: `Property Management (${assumptions.managementFee}% of collected rent)`, value: assumptions.monthlyRent * 12 * (1 - assumptions.vacancyRate / 100) * assumptions.managementFee / 100 },
            { label: `Vacancy Allowance (${assumptions.vacancyRate}% of rent)`, value: assumptions.monthlyRent * 12 * assumptions.vacancyRate / 100 },
            { label: 'Total Annual Expenses', value: metrics.year1Expenses, type: 'total' }
          ]}
          formatValue={(v) => typeof v === 'number' ? formatCurrency(v) : v}
        />

        <p className="text-gray-600 text-sm mt-4 mb-6">
          These expenses total <strong>{((metrics.year1Expenses / (assumptions.monthlyRent * 12)) * 100).toFixed(0)}%</strong> of
          gross rent—meaning only <strong>{(100 - (metrics.year1Expenses / (assumptions.monthlyRent * 12)) * 100).toFixed(0)}%</strong> flows
          through as net operating income. This is typical for high-value coastal California properties where property taxes
          (post-Prop 19) and insurance are substantial.
        </p>

        <p className="text-gray-600 text-sm mb-6">
          <strong>Important context:</strong> This expense ratio only describes <em>cash flow</em>. The full rental return
          also includes property appreciation (which doesn't show up in annual cash flow) and depreciation tax benefits
          (which reduce your taxable income by ~{formatCurrency(Math.round(assumptions.propertyValue * 0.25 / 27.5))}/year). When you factor in
          appreciation at {assumptions.propertyAppreciation}% real, the total return picture is much better than the cash flow alone suggests.
        </p>

        <CalloutBox type="warning" title="Insurance Cost Uncertainty">
          <p>
            The {formatCurrency(assumptions.insuranceCost)}/year insurance estimate reflects the current California coastal market,
            but <strong>this market is in crisis</strong>. Major insurers are leaving the state, and some areas are seeing
            50-100% premium increases. You may need to use the CA FAIR Plan (insurer of last resort) with supplemental
            coverage, which could cost $10,000-$15,000/year or more. Factor in uncertainty here.
          </p>
        </CalloutBox>
      </ReportSubsection>

      <ReportSubsection title="Initial Setup Costs">
        <p className="text-gray-700 mb-4">
          Before renting, you'll likely need to prepare the property. The model includes <strong>{formatCurrency(assumptions.rentalSetupCosts)}</strong> in
          one-time setup costs, which covers:
        </p>

        <ul className="list-disc list-inside text-gray-600 space-y-1 mb-4">
          <li><strong>Deep cleaning and minor repairs:</strong> Making the property rent-ready</li>
          <li><strong>Safety compliance:</strong> Smoke detectors, carbon monoxide detectors, any code requirements</li>
          <li><strong>Cosmetic updates:</strong> Paint touch-ups, minor fixes that affect rentability</li>
          <li><strong>Marketing:</strong> Professional photos, listing fees</li>
        </ul>

        <p className="text-gray-600 text-sm">
          This assumes the property is in generally good condition. If significant updates are needed (new flooring,
          appliance replacement, bathroom refresh), costs could be $30,000-$50,000+. Conversely, if the property
          is already move-in ready, setup costs might be minimal ($5,000 or less).
        </p>
      </ReportSubsection>

      <ReportSubsection title="A Note on Maintenance Costs">
        <p className="text-gray-700 mb-4">
          The conventional "1% of property value" rule would suggest ~$21,600/year in maintenance for this property.
          That figure comes from average-priced homes, but <strong>maintenance costs don't scale linearly with property value</strong>—a
          $2M Newport Beach home doesn't cost 10x more to maintain than a $200K Midwest home.
        </p>

        <p className="text-gray-700 mb-4">
          Research supports using a lower percentage for high-value properties:
        </p>

        <ul className="list-disc list-inside text-gray-600 space-y-1 mb-4">
          <li><strong>Census Bureau data:</strong> More than half of owners of older homes spend less than 1% annually</li>
          <li><strong>Per-square-foot data:</strong> At $0.90-$1.27/sqft for older homes, this ~1,500 sqft property would be $1,400-$1,900/year (even doubled for CA labor = $2,800-$3,800)</li>
          <li><strong>Actual routine costs:</strong> Landscaping, HVAC service, pest control, minor repairs = $5,000-$12,000/year</li>
        </ul>

        <p className="text-gray-700 mb-4">
          We use <strong>{assumptions.maintenanceRate}% ({formatCurrency(assumptions.propertyValue * assumptions.maintenanceRate / 100)}/year)</strong> as
          the baseline—appropriate for routine annual maintenance if the property is in good condition. This covers landscaping,
          regular service calls, and minor repairs. It does <em>not</em> include capital reserves for major system replacements
          (roof, HVAC, water heater), which would be separate one-time expenses if needed.
        </p>

        <DataTable
          columns={[
            { key: 'scenario', header: 'Maintenance Scenario', align: 'left' },
            { key: 'rate', header: 'Rate', align: 'right' },
            { key: 'annual', header: 'Annual Cost', align: 'right' },
            { key: 'impact', header: '10-Year Impact', align: 'right' }
          ]}
          data={(() => {
            const currentRate = assumptions.maintenanceRate / 100;
            const calcImpact = (rate: number) => {
              const diff = (rate - currentRate) * assumptions.propertyValue * 10 * 0.7; // ~70% after-tax impact
              if (Math.abs(diff) < 1) return '—';
              return diff > 0 ? `-${formatCurrency(Math.abs(diff))}` : `+${formatCurrency(Math.abs(diff))}`;
            };
            return [
              { scenario: 'Minimal (new/renovated)', rate: '0.25%', annual: formatCurrency(assumptions.propertyValue * 0.0025), impact: calcImpact(0.0025) },
              { scenario: 'Well maintained', rate: '0.5%', annual: formatCurrency(assumptions.propertyValue * 0.005), impact: calcImpact(0.005) },
              { scenario: 'Higher maintenance', rate: '1.0%', annual: formatCurrency(assumptions.propertyValue * 0.01), impact: calcImpact(0.01) },
              { scenario: 'Major systems aging', rate: '1.5%', annual: formatCurrency(assumptions.propertyValue * 0.015), impact: calcImpact(0.015) }
            ];
          })()}
          highlightRow={(row) => row.rate === `${assumptions.maintenanceRate}%`}
          bordered
        />

        <p className="text-gray-600 text-sm mt-4">
          Impact shows the approximate effect on your 10-year outcome vs. the baseline, accounting for reduced rental income
          and less money compounding in stocks. Higher maintenance reduces your investable cash flow each year.
        </p>
      </ReportSubsection>

      <ReportSubsection title="Year 1 Cash Flow">
        <p className="text-gray-700 mb-4">
          Here's how the numbers flow from gross rent to investable income:
        </p>

        {(() => {
          const annualDepreciation = Math.round(assumptions.propertyValue * 0.25 / 27.5);
          const taxableIncome = Math.max(0, metrics.year1NetIncome - annualDepreciation);
          const incomeTax = taxableIncome * assumptions.rentalIncomeTaxRate / 100;
          const afterTaxCashFlow = metrics.year1NetIncome - incomeTax;

          return (
            <CalculationBreakdown
              steps={[
                { label: 'Gross Annual Rent', value: assumptions.monthlyRent * 12 },
                { label: 'Total Expenses', value: metrics.year1Expenses, type: 'subtract' },
                { label: 'Net Operating Income (NOI)', value: metrics.year1NetIncome, type: 'subtotal' },
                { label: `Depreciation Deduction`, value: annualDepreciation, type: 'info' },
                { label: `Taxable Income (NOI − depreciation)`, value: taxableIncome, type: 'info' },
                { label: `Income Tax (${assumptions.rentalIncomeTaxRate}% of taxable income)`, value: incomeTax, type: 'subtract' },
                { label: 'After-Tax Cash Flow', value: afterTaxCashFlow, type: 'total' }
              ]}
              formatValue={(v) => typeof v === 'number' ? formatCurrency(v) : v}
            />
          );
        })()}

        <p className="text-gray-600 text-sm mt-4">
          This after-tax cash flow gets reinvested in stocks each year, compounding alongside your property appreciation.
          The "Keep & Rent" final value = Property Value + Cumulative Invested Rental Income (grown at stock returns).
        </p>
      </ReportSubsection>

      <ReportSubsection title="But What If You Need Income?">
        <p className="text-gray-700 mb-4">
          The analysis above assumes you'll <em>reinvest</em> the rental income. But what if you need that money
          to live on? This completely changes the comparison.
        </p>

        {(() => {
          const annualDepr = Math.round(assumptions.propertyValue * 0.25 / 27.5);
          const taxableInc = Math.max(0, metrics.year1NetIncome - annualDepr);
          const tax = taxableInc * assumptions.rentalIncomeTaxRate / 100;
          const afterTaxIncome = metrics.year1NetIncome - tax;

          return (
            <ComparisonGrid
              items={[
                {
                  label: 'Keep & Rent: Spendable Income',
                  value: `${formatCurrency(afterTaxIncome)}/year`,
                  sublabel: 'After-tax rental income (grows slowly with rent)'
                },
                {
                  label: 'Sell & Invest: 4% Withdrawal',
                  value: `${formatCurrency(metrics.saleProceeds * 0.04)}/year`,
                  sublabel: 'Pre-tax; ~$65-70K after taxes on gains/dividends',
                  highlighted: true
                }
              ]}
            />
          );
        })()}

        {(() => {
          const annualDepr = Math.round(assumptions.propertyValue * 0.25 / 27.5);
          const taxableInc = Math.max(0, metrics.year1NetIncome - annualDepr);
          const tax = taxableInc * assumptions.rentalIncomeTaxRate / 100;
          const afterTaxIncome = metrics.year1NetIncome - tax;
          const incomeMultiple = (metrics.saleProceeds * 0.04 / afterTaxIncome).toFixed(1);

          return (
            <CalloutBox type="warning" title="The Income Gap Is Huge">
              <p className="mb-2">
                If your goal is <strong>income</strong>, selling wins decisively. A 4% withdrawal from a ~{formatCurrency(metrics.saleProceeds)} portfolio
                provides roughly <strong>{formatCurrency(metrics.saleProceeds * 0.04)}/year</strong>—about{' '}
                <strong>{incomeMultiple}x more</strong> than
                the rental's after-tax cash flow.
              </p>
              <p>
                The rental only wins on income if you believe the property will appreciate fast enough that you could
                sell it later and have even more to withdraw from. That's a bet on Newport Beach outperformance.
              </p>
            </CalloutBox>
          );
        })()}

        <p className="text-gray-700 mt-4">
          <strong>If you plan to spend the rental income:</strong> The 10-year "Keep & Rent" value drops significantly
          because there's no reinvested income compounding. You'd end with just the appreciated property value (~{formatCurrency(metrics.year10PropertyValue)})
          versus ~{formatCurrency(metrics.year10SellValue)} from selling and investing (even after withdrawing 4% annually).
        </p>

        <p className="text-gray-600 text-sm mt-4">
          The rest of this analysis assumes reinvestment—the scenario that gives renting its best chance.
          If you're planning to spend the income, the math tilts further toward selling.
        </p>
      </ReportSubsection>

      <ReportSubsection title="The Tax Benefits (With Important Caveats)">
        <p className="text-gray-700 mb-4">
          Here's where rental properties get interesting—but also where the math gets complicated. The IRS lets
          you deduct "depreciation"—pretending the building is wearing out over 27.5 years, even though it's
          probably appreciating. However, there are two major limitations:
        </p>

        <div className="bg-gray-50 border border-gray-200 p-4 mb-4">
          <p className="font-semibold text-gray-900 mb-2">Limitation 1: Land vs. Building Ratio</p>
          <p className="text-gray-700 text-sm mb-2">
            You can only depreciate the <em>building</em>, not the land. In Newport Beach, where a 1954 house
            sits on prime coastal land, the land is typically <strong>75-80% of the value</strong>. A realistic
            split for this property:
          </p>
          <ComparisonGrid
            items={[
              { label: 'Land Value (~75%)', value: formatCurrency(assumptions.propertyValue * 0.75) },
              { label: 'Building Value (~25%)', value: formatCurrency(assumptions.propertyValue * 0.25) }
            ]}
          />
        </div>

        <StatCard
          label="Realistic Annual Depreciation"
          value={formatCurrency(assumptions.propertyValue * 0.25 / 27.5)}
          sublabel="Based on 25% building value for coastal Newport Beach"
        />

        <div className="bg-gray-50 border border-gray-200 p-4 mt-4 mb-4">
          <p className="font-semibold text-gray-900 mb-2">Limitation 2: Passive Activity Loss Rules</p>
          <p className="text-gray-700 text-sm">
            Rental losses (including depreciation) are "passive" and can only offset passive income for most
            taxpayers. There's a $25,000 exception for active participants, but it <strong>phases out completely</strong>
            for households with modified AGI above ~$150,000. If your income exceeds this threshold, the depreciation
            benefit may be <strong>suspended</strong>—carried forward to future years rather than providing annual tax savings.
          </p>
        </div>

        <p className="text-gray-700 mb-4">
          At ~{formatCurrency(Math.round(assumptions.propertyValue * 0.25 / 27.5))} annual depreciation (realistic for this property), the <em>potential</em> tax savings is
          about {formatCurrency(Math.round(assumptions.propertyValue * 0.25 / 27.5 * 0.30))}/year if you can use the deduction. But for high-income households, this benefit may be
          largely theoretical until you sell.
        </p>

        <p className="text-gray-600 text-sm">
          There is another catch: when you sell, you'll owe "depreciation recapture" tax on all the depreciation
          you claimed—25% federal <em>plus</em> California's ~13% (CA taxes gains as ordinary income). A 1031
          exchange can defer this, but adds complexity and keeps you locked in real estate.
        </p>
      </ReportSubsection>

      <ReportSubsection title="How We Get to the Final Number">
        <p className="text-gray-700 mb-4">
          The "Keep & Rent" calculation has two parts: first we calculate what you <em>accumulate</em> over 10 years,
          then we subtract what it <em>costs to exit</em> (taxes and fees when you eventually sell).
        </p>

        <p className="font-semibold text-gray-800 mb-2">Part 1: Building Wealth Over 10 Years</p>
        <CalculationBreakdown
          steps={[
            { label: 'Starting Property Value', value: assumptions.propertyValue },
            { label: `× Appreciation (${assumptions.propertyAppreciation}% real) over 10 years`, value: `× ${Math.pow(1 + assumptions.propertyAppreciation / 100, 10).toFixed(3)}`, type: 'info' },
            { label: 'Year 10 Property Value', value: metrics.year10PropertyValue, type: 'subtotal' },
            { label: `+ Invested Rental Income Portfolio`, value: finalYear?.rentScenario.cumulativeReinvestedIncome || 0 },
            { label: 'Setup Costs', value: assumptions.rentalSetupCosts, type: 'subtract' },
            { label: 'Gross Equity (before exit costs)', value: metrics.year10RentGrossEquity, type: 'subtotal' }
          ]}
          formatValue={(v) => typeof v === 'number' ? formatCurrency(v) : String(v)}
        />

        <p className="text-gray-600 text-sm mt-2 mb-6">
          Rental income is reinvested at the same 80/20 blended rate ({((assumptions.stockReturns * 0.8) + (assumptions.bondReturns * 0.2)).toFixed(1)}%) used in the sell scenario,
          ensuring an apples-to-apples comparison.
        </p>

        <p className="font-semibold text-gray-800 mb-2">Part 2: Exit Costs When You Sell</p>
        <p className="text-gray-700 mb-4">
          To compare fairly with "Sell & Invest," we need to account for what it costs to liquidate the property.
          You can't spend home equity without selling (or borrowing against it).
        </p>

        <CalculationBreakdown
          steps={[
            { label: 'Gross Equity', value: metrics.year10RentGrossEquity },
            { label: `Selling Costs (~${assumptions.sellingCosts}% of property value)`, value: finalYear?.rentScenario.sellingCostsAtExit || 0, type: 'subtract' },
            { label: 'Depreciation Recapture Tax (37% on accumulated depreciation)', value: finalYear?.rentScenario.depreciationRecaptureTax || 0, type: 'subtract' },
            { label: 'Capital Gains Tax (on post-inheritance appreciation)', value: finalYear?.rentScenario.capitalGainsTax || 0, type: 'subtract' },
            { label: 'Net Liquidation Value', value: metrics.year10RentValue, type: 'total' }
          ]}
          formatValue={(v) => typeof v === 'number' ? formatCurrency(v) : String(v)}
        />

        <CalloutBox type="info" title="Understanding the Exit Costs">
          <ul className="list-disc list-inside space-y-2 text-sm">
            <li>
              <strong>Selling costs (~{formatCurrency(finalYear?.rentScenario.sellingCostsAtExit || 0)}):</strong> Agent commissions,
              closing costs, transfer taxes. Same ~6% you'd pay selling now.
            </li>
            <li>
              <strong>Depreciation recapture (~{formatCurrency(finalYear?.rentScenario.depreciationRecaptureTax || 0)}):</strong> The IRS
              requires you to pay back the tax benefit from depreciation deductions at a 25% federal rate (+ ~12% CA).
              Over 10 years, you'll claim ~{formatCurrency(Math.round(assumptions.propertyValue * 0.25 / 27.5 * 10))} in depreciation.
            </li>
            <li>
              <strong>Capital gains (~{formatCurrency(finalYear?.rentScenario.capitalGainsTax || 0)}):</strong> Tax on the appreciation
              <em>after</em> you inherited (step-up basis protects the historical gains). At ~{assumptions.capitalGainsRate}% combined
              federal + CA rate on ~{formatCurrency(metrics.year10PropertyValue - assumptions.propertyValue)} of appreciation.
            </li>
          </ul>
        </CalloutBox>
      </ReportSubsection>

      <div className="bg-gray-100 p-6 mt-8">
        <h4 className="font-bold text-gray-900 text-lg mb-2">10-Year Outcome: Keep & Rent</h4>
        <p className="text-3xl font-bold text-gray-900">{formatCurrency(metrics.year10RentValue)}</p>
        <p className="text-sm text-gray-600 mt-1">
          Net liquidation value: what you'd walk away with after selling property, paying all taxes and fees
        </p>
        <p className="text-xs text-gray-500 mt-2">
          Gross equity {formatCurrency(metrics.year10RentGrossEquity)} − exit costs {formatCurrency(metrics.year10RentGrossEquity - metrics.year10RentValue)}
        </p>
      </div>

      <ReportSubsection title="What If: Optimistic Rent Scenarios">
        <p className="text-gray-700 mb-4">
          How much does higher rent or faster rent growth change the outcome? Here's a quick comparison
          using the current scenario assumptions:
        </p>

        <DataTable
          columns={[
            { key: 'label', header: 'Scenario', align: 'left', render: (_, row: typeof scenarios[0]) => (
              <>
                <span className="font-medium">{row.label}</span>
                <span className="text-gray-500 text-xs block">{row.desc}</span>
              </>
            )},
            { key: 'rent', header: 'Monthly Rent', align: 'right', render: (_, row: typeof scenarios[0]) => `$${row.rent.toLocaleString()}` },
            { key: 'growth', header: 'Rent Growth', align: 'right', render: (_, row: typeof scenarios[0]) => `${row.growth}%` },
            { key: 'total', header: '10-Year Total', align: 'right', render: (_, row: typeof scenarios[0]) => formatCurrency(calcRentalOutcome(row.rent, row.growth)) },
            { key: 'diff', header: 'vs Baseline', align: 'right', render: (_, row: typeof scenarios[0]) => {
              const diff = calcRentalOutcome(row.rent, row.growth) - baseline;
              return diff === 0 ? '—' : <span className={diff > 0 ? 'font-semibold' : ''}>+{formatCurrency(diff)}</span>;
            }}
          ]}
          data={scenarios}
          highlightRow={(_, index) => index === 0}
          bordered
        />

        <CalloutBox type="info" title="The Impact Is Meaningful But Not Transformative">
          <p className="mb-2">
            Going from ${(baseRent / 1000).toFixed(0)}K to ${((baseRent + 1000) / 1000).toFixed(0)}K/month adds roughly <strong>+{formatCurrency(calcRentalOutcome(baseRent + 1000, assumptions.rentGrowth) - baseline)}</strong> to your 10-year outcome—about
            a <strong>{(((calcRentalOutcome(baseRent + 1000, assumptions.rentGrowth) - baseline) / baseline) * 100).toFixed(1)}%</strong> improvement.
          </p>
          <p className="mb-2">
            The "both" scenario (optimistic rent + growth) adds <strong>+{formatCurrency(calcRentalOutcome(baseRent + 1000, 2.0) - baseline)}</strong>—a{' '}
            <strong>{(((calcRentalOutcome(baseRent + 1000, 2.0) - baseline) / baseline) * 100).toFixed(1)}%</strong> boost.
          </p>
          <p>
            These are nice improvements, but they don't dramatically change which option wins. The property appreciation
            assumption matters far more than rent optimization.
          </p>
        </CalloutBox>
      </ReportSubsection>
    </ReportSection>
  );
};
